import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  Drawer,
  FormControlLabel,
  Grid,
  IconButton,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import {
  getUserById,
  updateUserPermissions,
  updateProveedoresPermitidos,
} from "../../api/User/userApi";
import {
  mapUserToPermissionState,
  parseProveedoresPermitidosJson,
  sanitizePermissionsForSave,
  getUserCountryMeta,
  USER_PERMISSION_KEYS,
  USER_PERMISSION_GROUPS,
} from "../../utils/userPermissionsUtils";
import {
  pageFieldSx,
  pageMobileCardSx,
  pagePrimaryButtonSx,
} from "../../styles/pageModernStyles";

const PERMISSIONS = USER_PERMISSION_KEYS;

const AVATAR_PALETTE = [
  "#00897b",
  "#1565c0",
  "#6a1b9a",
  "#c62828",
  "#ef6c00",
  "#2e7d32",
  "#00838f",
  "#ad1457",
  "#4527a0",
  "#00695c",
  "#283593",
  "#f9a825",
  "#5d4037",
  "#37474f",
];

function colorFromString(value = "") {
  const str = String(value);
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
}

function permCountTone(count) {
  if (count <= 0) return { color: "#c62828", label: "Sin acceso" };
  if (count < 10) return { color: "#ef6c00", label: "Básico" };
  if (count < 25) return { color: "#1565c0", label: "Medio" };
  if (count < 40) return { color: "#00897b", label: "Amplio" };
  return { color: "#6a1b9a", label: "Alto" };
}

function userInitials(username = "") {
  const parts = String(username).trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return String(username).slice(0, 2).toUpperCase() || "?";
}

function formatPermissionLabel(perm) {
  if (perm === "isAdmin") return "Admin";
  if (perm === "Permiso_NMLFinanzas") return "NML Finanzas";
  if (perm === "Permisos_Expected_CP") return "Expected CP (editar bloqueadas)";
  if (perm === "Permiso_Reuniones") return "Reuniones";
  if (perm === "Permiso_Reuniones_VerTodas") return "Reuniones (ver todas)";
  if (perm === "Permiso_Tpv") return "TPV / Tienda";
  if (perm === "Permiso_Courier_Solicitar") return "Courier solicitar";
  if (perm === "Permiso_Courier_Terminar") return "Courier terminar";
  if (perm === "Permiso_NmlSuzhou") return "NML Suzhou";
  if (perm === "Permiso_NmlTurkey") return "NML Turquía";
  if (perm === "Permiso_NmlGuangzhou") return "NML Guangzhou";
  if (perm === "Permiso_NmlSuzhouCliente") return "NML CP (cliente)";
  if (perm === "Permiso_Skills") return "Skills (fichas)";
  if (perm === "Permiso_TareasAgentes") return "Tareas agentes";
  if (perm === "Permiso_PresentacionesAI") return "Presentaciones AI";
  return perm.replace(/^Permiso_/, "").replace(/_/g, " ");
}

const UserPermissionCard = ({
  user,
  open,
  onToggle,
  initialPermissions,
  onSaved,
}) => {
  const theme = useTheme();
  const [permissions, setPermissions] = useState(initialPermissions);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [proveedoresPermitidos, setProveedoresPermitidos] = useState("");
  const [jsonError, setJsonError] = useState(null);

  useEffect(() => {
    if (!open) {
      setPermissions(initialPermissions);
    }
  }, [initialPermissions, open]);

  const permCount = useMemo(
    () => Object.values(permissions).filter(Boolean).length,
    [permissions],
  );

  const isAdmin = Boolean(permissions?.isAdmin);
  const country = useMemo(() => getUserCountryMeta(user), [user]);
  const avatarColor = useMemo(
    () => colorFromString(user.username || user.id),
    [user.username, user.id],
  );
  const tone = permCountTone(permCount);
  const accent = isAdmin ? "#f9a825" : tone.color;

  useEffect(() => {
    if (!open) return;

    const load = async () => {
      setLoading(true);
      try {
        const data = await getUserById(user.id);
        const perms = mapUserToPermissionState(data, PERMISSIONS);
        setPermissions(perms);
        setProveedoresPermitidos(
          data.ProveedoresPermitidos
            ? JSON.stringify(JSON.parse(data.ProveedoresPermitidos), null, 2)
            : "",
        );
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [open, user.id]);

  const handleChange = (perm) => {
    setPermissions((prev) => ({
      ...prev,
      [perm]: !prev[perm],
    }));
  };

  const setGroupAll = (keys, value) => {
    setPermissions((prev) => {
      const next = { ...prev };
      keys.forEach((key) => {
        next[key] = value;
      });
      return next;
    });
  };

  const handleSave = async () => {
    setLoading(true);
    const sanitized = sanitizePermissionsForSave(permissions, PERMISSIONS);
    const parsed = parseProveedoresPermitidosJson(proveedoresPermitidos);

    if (!parsed.ok) {
      setJsonError(parsed.error);
      setLoading(false);
      return;
    }

    try {
      await Promise.all([
        updateUserPermissions(user.id, sanitized),
        updateProveedoresPermitidos(user.id, parsed.value),
      ]);

      onSaved?.(sanitized);
      setSnackbarOpen(true);
      onToggle();
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        "Error saving permissions";
      setJsonError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box
        onClick={onToggle}
        sx={{
          ...pageMobileCardSx(theme, { accentColor: accent }),
          pl: 2,
          pr: 1.5,
          py: 1.5,
          cursor: "pointer",
          transition: "transform 0.15s ease, box-shadow 0.15s ease",
          "&:active": { transform: "scale(0.99)" },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ minWidth: 0 }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 800,
                fontSize: "0.75rem",
                color: "#fff",
                bgcolor: avatarColor,
                border: "2px solid",
                borderColor: alpha(avatarColor, 0.35),
                boxShadow: `0 4px 12px ${alpha(avatarColor, 0.35)}`,
              }}
            >
              {userInitials(user.username)}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                fontWeight={800}
                sx={{ fontSize: "0.95rem", lineHeight: 1.2 }}
                noWrap
              >
                {user.username}
              </Typography>
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                useFlexGap
                flexWrap="wrap"
                sx={{ mt: 0.15, rowGap: 0.25 }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 600 }}
                >
                  ID {user.id}
                </Typography>
                <Box
                  component="span"
                  sx={{
                    fontSize: "0.62rem",
                    fontWeight: 800,
                    px: 0.6,
                    py: 0.1,
                    borderRadius: 1,
                    bgcolor: alpha(country.color, 0.12),
                    color: country.color,
                    border: `1px solid ${alpha(country.color, 0.32)}`,
                    whiteSpace: "nowrap",
                  }}
                >
                  {country.flag} {country.label}
                </Box>
                {isAdmin ? (
                  <Box
                    component="span"
                    sx={{
                      fontSize: "0.62rem",
                      fontWeight: 800,
                      px: 0.6,
                      py: 0.1,
                      borderRadius: 1,
                      bgcolor: alpha("#f9a825", 0.16),
                      color: "#f57f17",
                      border: `1px solid ${alpha("#f9a825", 0.4)}`,
                    }}
                  >
                    ADMIN
                  </Box>
                ) : null}
              </Stack>
            </Box>
          </Stack>
          <Badge
            badgeContent={permCount}
            max={99}
            sx={{
              flexShrink: 0,
              mr: 0.5,
              "& .MuiBadge-badge": {
                fontSize: "0.68rem",
                fontWeight: 800,
                minWidth: 20,
                height: 20,
                bgcolor: tone.color,
                color: "#fff",
              },
            }}
          >
            <PersonOutlineOutlinedIcon
              sx={{ color: alpha(tone.color, 0.85) }}
            />
          </Badge>
        </Stack>
      </Box>

      <Drawer
        anchor="right"
        open={open}
        onClose={onToggle}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: "min(760px, 96vw)" },
            maxWidth: "100%",
            bgcolor: theme.palette.background.default,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 2,
            px: { xs: 1.5, sm: 2 },
            py: 1.5,
            bgcolor: theme.palette.background.paper,
            borderBottom: "1px solid",
            borderColor: "divider",
            boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.06)}`,
          }}
        >
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            gap={1}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ fontWeight: 700 }}
              >
                Permissions
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mt: 0.25 }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 800,
                    fontSize: "0.72rem",
                    color: "#fff",
                    bgcolor: avatarColor,
                    boxShadow: `0 4px 12px ${alpha(avatarColor, 0.35)}`,
                    flexShrink: 0,
                  }}
                >
                  {userInitials(user.username)}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="h6"
                    fontWeight={800}
                    sx={{ lineHeight: 1.2 }}
                    noWrap
                  >
                    {user.username}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ID {user.id} · {country.flag} {country.label}
                  </Typography>
                </Box>
              </Stack>
              <Stack
                direction="row"
                spacing={0.75}
                sx={{ mt: 1 }}
                flexWrap="wrap"
                useFlexGap
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    bgcolor: alpha(tone.color, 0.14),
                    color: tone.color,
                    border: `1px solid ${alpha(tone.color, 0.35)}`,
                  }}
                >
                  {permCount} permisos · {tone.label}
                </Typography>
                {isAdmin ? (
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 800,
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      bgcolor: alpha("#f9a825", 0.16),
                      color: "#f57f17",
                      border: `1px solid ${alpha("#f9a825", 0.4)}`,
                    }}
                  >
                    ADMIN
                  </Typography>
                ) : null}
              </Stack>
            </Box>
            <IconButton onClick={onToggle} aria-label="Close" edge="end">
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            p: { xs: 1.5, sm: 2 },
            pb: { xs: 10, sm: 2 },
          }}
        >
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Stack spacing={1.5}>
                {USER_PERMISSION_GROUPS.map((group) => {
                  const active = group.keys.filter(
                    (key) => permissions[key],
                  ).length;
                  const total = group.keys.length;
                  const hasActive = active > 0;
                  return (
                    <Box
                      key={group.id}
                      sx={{
                        ...pageMobileCardSx(theme, {
                          active: hasActive,
                          accentColor: hasActive
                            ? theme.palette.success.main
                            : theme.palette.divider,
                        }),
                        p: 1.5,
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={1}
                        sx={{ mb: 1 }}
                      >
                        <Stack
                          direction="row"
                          spacing={0.75}
                          alignItems="center"
                          sx={{ minWidth: 0 }}
                        >
                          <Typography
                            variant="subtitle2"
                            fontWeight={800}
                            noWrap
                          >
                            {group.label}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 800,
                              px: 0.8,
                              py: 0.15,
                              borderRadius: 1,
                              bgcolor: hasActive
                                ? alpha(theme.palette.success.main, 0.14)
                                : alpha(theme.palette.text.secondary, 0.08),
                              color: hasActive
                                ? theme.palette.success.main
                                : theme.palette.text.secondary,
                              border: `1px solid ${
                                hasActive
                                  ? alpha(theme.palette.success.main, 0.35)
                                  : alpha(theme.palette.divider, 0.6)
                              }`,
                            }}
                          >
                            {active}/{total}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.5}>
                          <Button
                            size="small"
                            onClick={() => setGroupAll(group.keys, true)}
                            sx={{
                              minWidth: 0,
                              px: 0.9,
                              py: 0.2,
                              fontSize: "0.7rem",
                              fontWeight: 800,
                              textTransform: "none",
                            }}
                          >
                            Todos
                          </Button>
                          <Button
                            size="small"
                            onClick={() => setGroupAll(group.keys, false)}
                            sx={{
                              minWidth: 0,
                              px: 0.9,
                              py: 0.2,
                              fontSize: "0.7rem",
                              fontWeight: 800,
                              textTransform: "none",
                              color: "text.secondary",
                            }}
                          >
                            Ninguno
                          </Button>
                        </Stack>
                      </Stack>
                      <Grid container spacing={0.5}>
                        {group.keys.map((perm) => (
                          <Grid item xs={12} sm={6} key={perm}>
                            <FormControlLabel
                              sx={{
                                m: 0,
                                py: 0.35,
                                px: 0.75,
                                width: "100%",
                                borderRadius: 1.5,
                                alignItems: "flex-start",
                                bgcolor: permissions[perm]
                                  ? alpha(theme.palette.success.main, 0.06)
                                  : "transparent",
                                border: "1px solid",
                                borderColor: permissions[perm]
                                  ? alpha(theme.palette.success.main, 0.18)
                                  : alpha(theme.palette.divider, 0.45),
                                "& .MuiFormControlLabel-label": {
                                  fontSize: "0.8125rem",
                                  fontWeight: permissions[perm] ? 700 : 500,
                                  lineHeight: 1.35,
                                  pt: 0.35,
                                },
                              }}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={!!permissions[perm]}
                                  onChange={() => handleChange(perm)}
                                  sx={{ py: 0.5 }}
                                />
                              }
                              label={formatPermissionLabel(perm)}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  );
                })}
              </Stack>

              <Divider sx={{ my: 2.5 }} />

              <Typography variant="subtitle2" fontWeight={800} gutterBottom>
                ProveedoresPermitidos (JSON)
              </Typography>

              <TextField
                multiline
                minRows={5}
                fullWidth
                value={proveedoresPermitidos}
                onChange={(e) => {
                  setProveedoresPermitidos(e.target.value);
                  setJsonError(null);
                }}
                error={!!jsonError}
                helperText={jsonError || "Edit JSON directly"}
                sx={{
                  ...pageFieldSx(theme),
                  "& textarea": { fontFamily: "monospace", fontSize: "0.8rem" },
                }}
              />
            </>
          )}
        </Box>

        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            zIndex: 2,
            px: { xs: 1.5, sm: 2 },
            py: 1.5,
            bgcolor: theme.palette.background.paper,
            borderTop: "1px solid",
            borderColor: "divider",
            boxShadow: `0 -4px 16px ${alpha(theme.palette.common.black, 0.06)}`,
          }}
        >
          <Button
            variant="contained"
            fullWidth
            startIcon={
              loading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <SaveOutlinedIcon />
              )
            }
            disabled={loading}
            onClick={handleSave}
            sx={pagePrimaryButtonSx(theme)}
          >
            Save permissions
          </Button>
        </Box>
      </Drawer>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        message="Permissions updated"
        onClose={() => setSnackbarOpen(false)}
      />
    </>
  );
};

export default UserPermissionCard;
